import { AgendaSpeakerDto } from '@linka/domain/dtos';
import { UpdateAgendaSpeakerRequest } from '@linka/domain/requests';
import {
  AccessDevice,
  AgendaDto,
  BelongingsDto,
  CategoryDto,
  CompanyCnpjLookupDto,
  CompanyEnvironmentsResponse,
  ContactRequest,
  ContactResponseDto,
  CouponDTO,
  CouponToggleRequest,
  CreateAgendaRequest,
  CreateBelongingsControlRequest,
  CreateCheckoutRequest,
  CreateCouponRequest,
  CreateEventCheckInRequest,
  CreateFormFieldRequest,
  CreateTeamMemberRequest,
  CreateTicketRequest,
  DashboardDto,
  EventCheckInCreatedDto,
  EventCheckoutDto,
  EventDto,
  EventImageDto,
  FormFieldDto,
  IntegrationConfigurationDto,
  IntegrationConfigurationRequest,
  IntegrationDto,
  ListPublishedEventsDto,
  ListResponseDto,
  ManageTicketFormFieldsRequest,
  ParticipantImportDto,
  ParticipantImportProgress,
  ParticipantImportResponse,
  ParticipantImportTemplateResponse,
  PecegePaySettingsDto,
  PrintBelongingsControlResponse,
  ProcessEventCheckoutRequest,
  PublicPaymentSettingsDto,
  RefreshRequest,
  SetTermsAndPoliticsRequest,
  SetTicketUniqueRegistrationRequest,
  SignInDto,
  SignInRequest,
  SoulBankDto,
  SoulGeographicDto,
  TeamMemberDto,
  TermsAndPoliticsDto,
  TicketDto,
  UpdateAgendaRequest,
  UpdateBelongingsControlRequest,
  UpdateCouponRequest,
  UpdateEventRequest,
  UpdateFormFieldRequest,
  UpdateImportParticipantsRequest,
  UpdateTeamMemberRequest,
  UpdateTicketRequest,
  UpdateTicketStatusRequest,
  ViacepAddressDto,
} from './generated/data-contracts';
import { httpRequest } from './http-client';

export const authController = {
  signIn: httpRequest<SignInRequest, SignInDto>('post', 'auth'),
  refreshToken: httpRequest<RefreshRequest, SignInDto>('post', 'auth/refresh'),
};
export const checkoutController = {
  getCheckoutById: httpRequest<unknown, EventCheckoutDto>('get', 'event/:eventId/checkout/:checkoutId'),
  createCheckout: httpRequest<CreateCheckoutRequest, EventCheckoutDto>('post', 'event/:eventId/checkout'),
  processCheckout: httpRequest<ProcessEventCheckoutRequest, EventCheckoutDto>(
    'post',
    'event/:eventId/checkout/:checkoutId/process',
  ),
};
export const eventController = {
  listEvents: httpRequest<unknown, ListPublishedEventsDto>('get', 'event'),
  getEventById: httpRequest<unknown, EventDto>('get', 'event/:eventId/details'),
  togglePrivacy: httpRequest('post', 'event/:eventId/privacy'),
  delete: httpRequest('delete', 'event/:eventId'),
  create: httpRequest<unknown, EventDto>('post', 'event'),
  publish: httpRequest('post', 'event/publish/:eventId'),
  unpublish: httpRequest('post', 'event/unpublish/:eventId'),
  update: httpRequest<UpdateEventRequest, EventDto>('put', 'event/:eventId'),
  updateBanner: httpRequest<FormData, EventDto>('put', 'event/:eventId/banner'),
  uploadImage: httpRequest<FormData, EventImageDto[]>('post', 'event/:eventId/images'),
  deleteImage: httpRequest<unknown, void>('delete', 'event/:eventId/images/:fileName'),
  dashboard: httpRequest<unknown, DashboardDto>('get', 'event/:eventId/dashboard'),
  initiateContractSession: httpRequest<unknown, { embedId: string; embedUrl?: string }>(
    'post',
    'event/:eventId/contract/session',
  ),
  getEventContract: httpRequest<unknown, { id: string; status: string } | null>('get', 'event/:eventId/contract'),
  confirmContract: httpRequest('post', 'event/:eventId/contract/confirm'),
  refuseContract: httpRequest('post', 'event/:eventId/contract/refuse'),
};

export const importParticipantsController = {
  listImportParticipantsProgress: httpRequest<unknown, ParticipantImportProgress>(
    'get',
    'event/:eventId/import/participant/progress',
  ),
  listImportParticipants: httpRequest<unknown, ListResponseDto<ParticipantImportDto>>(
    'get',
    'event/:eventId/import/participant',
  ),
  importParticipants: httpRequest<FormData, ParticipantImportResponse>('post', 'event/:eventId/import/participant'),
  reimportParticipants: httpRequest<unknown, Pick<ParticipantImportResponse, 'message'>>(
    'post',
    'event/:eventId/import/participant/:importId/reprocess',
  ),
  updateImportParticipants: httpRequest<UpdateImportParticipantsRequest, unknown>(
    'put',
    'event/:eventId/import/participant/:importId',
  ),
  delete: httpRequest<unknown, Pick<ParticipantImportResponse, 'message'>>(
    'delete',
    'event/:eventId/import/participant/:importId',
  ),
  importParticipantsTemplate: httpRequest<unknown, ParticipantImportTemplateResponse>(
    'get',
    'event/:eventId/import/participant/template',
  ),
};

export const integrationsController = {
  listIntegrations: httpRequest<unknown, IntegrationDto[]>('get', 'event/:eventId/integrations'),
  getSettingsByIntegrationId: httpRequest<unknown, IntegrationConfigurationDto>(
    'get',
    '/event/:eventId/integrations/:integrationId/configuration',
  ),
  createIntegration: httpRequest<IntegrationConfigurationRequest, IntegrationConfigurationDto>(
    'post',
    '/event/:eventId/integrations/:integrationId/configuration',
  ),
  updateIntegration: httpRequest<IntegrationConfigurationRequest, IntegrationConfigurationDto>(
    'put',
    '/event/:eventId/integrations/:integrationId/configuration',
  ),
  getSoulBanks: httpRequest<unknown, SoulBankDto[]>('get', 'integrations/soul/banks'),
  getSoulCountries: httpRequest<unknown, SoulGeographicDto[]>('get', 'integrations/soul/countries'),
  getSoulStates: httpRequest<unknown, SoulGeographicDto[]>('get', 'integrations/soul/states/:countryId'),
  getSoulCities: httpRequest<unknown, SoulGeographicDto[]>('get', 'integrations/soul/cities/:stateId'),
  viacep: httpRequest<unknown, ViacepAddressDto>('get', 'integrations/viacep/address/:zipCode'),
  getSoulAddress: httpRequest<unknown, SoulGeographicDto>('get', 'integrations/soul/address/:zipCode'),
  getCompanyByCnpj: httpRequest<unknown, CompanyCnpjLookupDto>('get', 'integrations/brasil-api/cnpj/:cnpj'),
};

export const settingsController = {
  listTermsAndPolitics: httpRequest<unknown, TermsAndPoliticsDto>('get', 'settings/terms-and-politics'),
  createTermsAndPolitics: httpRequest<SetTermsAndPoliticsRequest, TermsAndPoliticsDto>(
    'post',
    'settings/terms-and-politics/:language',
  ),
  createPecegePaySettings: httpRequest<PecegePaySettingsDto, PecegePaySettingsDto>(
    'post',
    'event/:eventId/settings/payment-details',
  ),
  getPecegePaySettings: httpRequest<unknown, PecegePaySettingsDto>('get', 'event/:eventId/settings/payment-details'),
  convertParticipants: httpRequest<unknown, unknown>(
    'post',
    'marketing/events/:eventId/convert-participants/:marketingIdentifier',
  ),
};

export const eventSettingsController = {
  getEventPaymentSettings: httpRequest<unknown, PublicPaymentSettingsDto>(
    'get',
    'event/:eventId/settings/public-payment-details',
  ),
};

export const orderController = {
  getOrderStatus: httpRequest<unknown, ReadableStream<Uint8Array>>('get', ':eventId/order/:id/status'),
};

export const agendaController = {
  listAgendas: httpRequest<unknown, ListResponseDto<AgendaDto>>('get', 'event/:eventId/agenda/details'),
  findById: httpRequest<unknown, AgendaDto>('get', 'event/:eventId/agenda/:agendaId'),
  delete: httpRequest('delete', 'event/:eventId/agenda/:agendaId'),
  create: httpRequest<CreateAgendaRequest, AgendaDto>('post', 'event/:eventId/agenda'),
  update: httpRequest<UpdateAgendaRequest, AgendaDto>('put', 'event/:eventId/agenda/:agendaId'),
};

export const speakerController = {
  list: httpRequest<unknown, ListResponseDto<AgendaSpeakerDto>>('get', 'event/:eventId/agenda/:agendaId/speaker'),
  findById: httpRequest<unknown, AgendaSpeakerDto>('get', 'event/:eventId/agenda/:agendaId/speaker/:speakerId'),
  update: httpRequest<UpdateAgendaSpeakerRequest, AgendaDto>(
    'put',
    'event/:eventId/agenda/:agendaId/speaker/:speakerId',
  ),
  create: httpRequest<FormData, AgendaDto>('post', 'event/:eventId/agenda/:agendaId/speaker'),
  delete: httpRequest('delete', 'event/:eventId/agenda/:agendaId/speaker/:speakerId'),
};

export const ticketController = {
  list: httpRequest<unknown, ListResponseDto<TicketDto>>('get', 'event/:eventId/ticket/admin'),
  findById: httpRequest<unknown, TicketDto>('get', 'event/:eventId/ticket/:ticketId'),
  update: httpRequest<UpdateTicketRequest, TicketDto>('put', 'event/:eventId/ticket/:ticketId'),
  updateStatus: httpRequest<UpdateTicketStatusRequest, TicketDto>('patch', 'event/:eventId/ticket/:ticketId/status'),
  setUniqueRegistration: httpRequest<SetTicketUniqueRegistrationRequest, TicketDto>(
    'patch',
    'event/:eventId/ticket/:ticketId/unique-registration',
  ),
  create: httpRequest<CreateTicketRequest, TicketDto>('post', 'event/:eventId/ticket'),
};

export const eventCouponController = {
  list: httpRequest<unknown, ListResponseDto<CouponDTO>>('get', 'event/:eventId/coupon'),
  create: httpRequest<CreateCouponRequest, CouponDTO>('post', 'event/:eventId/coupon'),
  delete: httpRequest('delete', 'event/:eventId/coupon/:couponId'),
  toggle: httpRequest<CouponToggleRequest, CouponDTO>('patch', 'event/:eventId/coupon/:couponId/status'),
  update: httpRequest<UpdateCouponRequest, CouponDTO>('put', 'event/:eventId/coupon/:couponId'),
  findById: httpRequest<unknown, CouponDTO>('get', 'event/:eventId/coupon/:couponId'),
};

export const teamMembersController = {
  delete: httpRequest('delete', 'events/:eventId/team-members/:memberId'),
  update: httpRequest<UpdateTeamMemberRequest, TeamMemberDto>('put', 'events/:eventId/team-members/:memberId'),
  create: httpRequest<CreateTeamMemberRequest, TeamMemberDto>('post', 'events/:eventId/team-members'),
  list: httpRequest<unknown, ListResponseDto<TeamMemberDto>>('get', 'events/:eventId/team-members'),
};

export const checkInController = {
  createCheckIn: httpRequest<CreateEventCheckInRequest, EventCheckInCreatedDto>('post', 'event/:eventId/check-in'),
};
export const formFieldController = {
  delete: httpRequest('delete', 'event/:eventId/form-field/:formFieldId'),
  update: httpRequest<UpdateFormFieldRequest, FormFieldDto>('put', 'event/:eventId/form-field/:formFieldId'),
  create: httpRequest<CreateFormFieldRequest, FormFieldDto>('post', 'event/:eventId/form-field'),
  enable: httpRequest<ManageTicketFormFieldsRequest, FormFieldDto>('post', 'event/:eventId/form-field/manage'),
  listByTicket: httpRequest<unknown, FormFieldDto[]>('get', 'event/:eventId/form-field/by-ticket/:ticketId'),
  listByEvent: httpRequest<unknown, Pick<FormFieldDto, 'id' | 'name' | 'label'>[]>('get', 'event/:eventId/form-field'),
};
export const uidController = {
  devices: httpRequest<unknown, ListResponseDto<AccessDevice>>('get', 'integrations/uid/access-devices?:ceId'),
  myCE: httpRequest<unknown, ListResponseDto<CompanyEnvironmentsResponse>>(
    'get',
    'integrations/uid/company-environments/my',
  ),
};

export const belongingsController = {
  createBelongings: httpRequest<CreateBelongingsControlRequest, BelongingsDto>(
    'post',
    'event/:eventId/belongings-control',
  ),
  listBelongings: httpRequest<
    unknown,
    ListResponseDto<BelongingsDto> & { checkedInCount: number; checkedOutCount: number }
  >('get', 'event/:eventId/belongings-control'),
  returnBelongs: httpRequest<UpdateBelongingsControlRequest, BelongingsDto>(
    'put',
    'event/:eventId/belongings-control/checked-out',
  ),
  printBelongs: httpRequest<UpdateBelongingsControlRequest, PrintBelongingsControlResponse>(
    'post',
    'event/:eventId/belongings-control/identification-tag',
  ),
};

export const contactController = {
  create: httpRequest<ContactRequest, ContactResponseDto>('post', '/contact'),
};

export const categoryController = {
  listCategories: httpRequest<unknown, ListResponseDto<CategoryDto>>('get', 'category'),
};
